'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Lock, Loader2, ArrowRight } from 'lucide-react';
import { useRouter } from 'next/navigation';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface LoginFormProps {
  onSwitchToRegister: () => void;
}

export default function LoginForm({ onSwitchToRegister }: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();

  const { register, handleSubmit, formState: { errors }, setError } = useForm<LoginFormData>({
    defaultValues: { rememberMe: false },
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    // Backend integration point: POST /api/auth/login
    await new Promise((r) => setTimeout(r, 1200));

    const validCredentials = [
      { email: 'player@brainbasket.io', password: 'Player@2026' },
      { email: 'admin@brainbasket.io', password: 'Admin@2026' },
      { email: 'creator@brainbasket.io', password: 'Creator@2026' },
    ];

    const isValid = validCredentials.some(
      (c) => c.email === data.email && c.password === data.password
    );

    if (!isValid) {
      setError('email', { message: 'Invalid credentials — use the demo accounts below to sign in' });
      setIsLoading(false);
      return;
    }

    toast.success('Welcome back to Brain Basket!');
    router.push('/user-dashboard');
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-foreground mb-1">Welcome back</h1>
        <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Sign in to your Brain Basket account</p>
      </div>

      {/* Email */}
      <div>
        <label className="block text-sm font-semibold mb-1.5 text-foreground" htmlFor="login-email">
          Email Address
        </label>
        <div className="relative">
          <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
          <input
            id="login-email"
            type="email"
            placeholder="your@email.com"
            className="w-full pl-10 pr-4 py-2.5 text-sm rounded-lg outline-none transition-all"
            style={{
              background: 'var(--input)',
              border: `1px solid ${errors.email ? '#F43F5E' : 'var(--border)'}`,
              color: 'var(--foreground)',
            }}
            onFocus={(e) => { if (!errors.email) e.target.style.borderColor = 'var(--primary)'; }}
            onBlur={(e) => { if (!errors.email) e.target.style.borderColor = 'var(--border)'; }}
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
            })}
          />
        </div>
        {errors.email && <p className="text-xs mt-1" style={{ color: '#F43F5E' }}>{errors.email.message}</p>}
      </div>

      {/* Password */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-sm font-semibold text-foreground" htmlFor="login-password">Password</label>
          <Link href="/" className="text-xs font-medium" style={{ color: '#A78BFA' }}>Forgot password?</Link>
        </div>
        <div className="relative">
          <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
          <input
            id="login-password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Your password"
            className="w-full pl-10 pr-10 py-2.5 text-sm rounded-lg outline-none transition-all"
            style={{
              background: 'var(--input)',
              border: `1px solid ${errors.password ? '#F43F5E' : 'var(--border)'}`,
              color: 'var(--foreground)',
            }}
            onFocus={(e) => { if (!errors.password) e.target.style.borderColor = 'var(--primary)'; }}
            onBlur={(e) => { if (!errors.password) e.target.style.borderColor = 'var(--border)'; }}
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 6, message: 'Password must be at least 6 characters' },
            })}
          />
          <button type="button" onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5" aria-label="Toggle password visibility">
            {showPassword
              ? <EyeOff size={16} style={{ color: 'var(--muted-foreground)' }} />
              : <Eye size={16} style={{ color: 'var(--muted-foreground)' }} />
            }
          </button>
        </div>
        {errors.password && <p className="text-xs mt-1" style={{ color: '#F43F5E' }}>{errors.password.message}</p>}
      </div>

      {/* Remember me */}
      <div className="flex items-center gap-2">
        <input
          id="remember-me"
          type="checkbox"
          className="w-4 h-4 rounded accent-primary"
          {...register('rememberMe')}
        />
        <label htmlFor="remember-me" className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
          Remember me for 30 days
        </label>
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={isLoading}
        className="btn-primary w-full justify-center py-3 text-base"
        style={{ opacity: isLoading ? 0.8 : 1, minWidth: '180px' }}
      >
        {isLoading ? (
          <><Loader2 size={18} className="animate-spin" /> Signing in...</>
        ) : (
          <>Sign In <ArrowRight size={18} /></>
        )}
      </button>

      {/* Divider */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
        <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>or continue with</span>
        <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
      </div>

      {/* Google */}
      <button type="button" className="btn-ghost w-full justify-center py-2.5">
        <svg width="18" height="18" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        Continue with Google
      </button>

      <p className="text-sm text-center" style={{ color: 'var(--muted-foreground)' }}>
        New to Brain Basket?{' '}
        <button type="button" onClick={onSwitchToRegister} className="font-semibold" style={{ color: '#A78BFA' }}>
          Create free account
        </button>
      </p>
    </form>
  );
}